import { Injectable, signal } from '@angular/core';

/** Visual/semantic flavor of a toast. Maps to a Bootstrap contextual color. */
export type ToastVariant = 'success' | 'error' | 'info';

export interface Toast {
	/** Monotonic id used for tracking + removal. */
	id: number;
	message: string;
	variant: ToastVariant;
	/** Auto-dismiss delay in ms; `0` keeps the toast until dismissed manually. */
	delay: number;
}

/**
 * Minimal, standalone, signals-based toast queue — the modern-Angular
 * replacement for the ng-bootstrap-backed toast in the main app.
 *
 * Non-blocking by design: `show` enqueues a toast and (unless `delay` is 0)
 * schedules its own removal, so callers never await it. Read {@link toasts}
 * from the container component to render the stack.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
	private readonly _toasts = signal<Toast[]>([]);
	readonly toasts = this._toasts.asReadonly();

	private nextId = 0;

	/** Enqueue a toast. Returns immediately; dismissal is scheduled internally. */
	show(message: string, variant: ToastVariant = 'info', delay = 4000): void {
		const id = this.nextId++;
		this._toasts.update((list) => [...list, { id, message, variant, delay }]);
		if (delay > 0) {
			setTimeout(() => this.remove(id), delay);
		}
	}

	/** Convenience: a short-lived success toast. */
	success(message: string, delay = 3000): void {
		this.show(message, 'success', delay);
	}

	/** Convenience: a longer-lived error toast (users need time to read failures). */
	error(message: string, delay = 6000): void {
		this.show(message, 'error', delay);
	}

	/** Convenience: an informational toast. */
	info(message: string, delay = 4000): void {
		this.show(message, 'info', delay);
	}

	remove(id: number): void {
		this._toasts.update((list) => list.filter((t) => t.id !== id));
	}
}
